import React, { useState, useContext } from 'react';
import { Formik, Form } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';
import { makeStyles, Grid, Tabs, Tab, CircularProgress } from '@material-ui/core';
import { FormServiceContext } from 'app/utils/contexts';
import notification from 'app/config/notification';
import AlertMessage from 'app/components/AlertMessage';
import SaveButton from 'app/components/SaveButton';
import FinalizeButton from 'app/components/FinalizeButton';
import ChangeEmailForm from './components/ChangeEmailForm';
import Documentation from 'app/components/Documentation';
import { filterDocumentsChangeEmail } from 'app/utils/forms/functions';
import styles from './styles';
const useStyles = makeStyles(styles);

/**
 * ChangeEmailRegistration component is responsible for handling the registration process for changing email.
 * It uses various hooks and context for state management.
 * It also uses Formik for form handling and validation.
 *
 * @returns {JSX.Element} - A React component.
 */
function ChangeEmailRegistration(props) {
    const classes = useStyles();
    const {
        initialValues,
        validationSchema,
        validateOnSave,
        onSubmit,
        validateForm,
        loadingDocuments,
        onSave,
        onCancel,
        mainColor,
        hideActionBtn,
        hideFinalizeBtn,
        isInternalForm,
        canEdit,
    } = useContext(FormServiceContext);

    const viewsIdentification = 0;
    const viewsDocumentation = 1;

    const [view, setView] = useState(viewsIdentification);

    const handleViewChange = (event, value) => {
        setView(value);
    };

    const renderTabs = () => {
        return (
            <Grid container spacing={3} className={classes.tabsContainer}>
                <Tabs
                    variant="scrollable"
                    className={classes.tabs}
                    value={view}
                    onChange={handleViewChange}
                    indicatorColor={mainColor ? mainColor : 'primary'}
                    textColor={mainColor ? mainColor : 'primary'}>
                    <Tab className={classes.tab} label={<FormattedMessage id="LABELS.MENU.TABS.IDENTIFICATION" />} />
                    {loadingDocuments && !isInternalForm && (
                        <Tab
                            className={classes.tab}
                            label={<CircularProgress size={12} className={classes.loading} />}
                        />
                    )}
                    {!isInternalForm && !loadingDocuments && (
                        <Tab className={classes.tab} label={<FormattedMessage id="LABELS.COMMONS.DOCUMENTATION" />} />
                    )}
                </Tabs>
            </Grid>
        );
    };

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues ?? {}}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                    await onSubmit(values);
                }}>
                {(formikProps) => {
                    return (
                        <Form className={classes.containerForm}>
                            {renderTabs()}

                            <AlertMessage isInternalForm={isInternalForm} />
                            {view === viewsIdentification && <ChangeEmailForm {...formikProps} intl={props.intl} />}
                            {view === viewsDocumentation && (
                                <Documentation
                                    {...formikProps}
                                    filterDocuments={(doc) => filterDocumentsChangeEmail(doc, formikProps.values)}
                                    intl={props.intl}
                                />
                            )}

                            <SaveButton
                                mainColor={mainColor}
                                hideActionBtn={hideActionBtn}
                                isInternalForm={isInternalForm}
                                canEdit={canEdit}
                                onCancel={() => onCancel(formikProps.values)}
                                onSave={() => {
                                    if (validateOnSave) {
                                        formikProps.validateForm().then((res) => {
                                            if (Object.keys(res).length <= 0) onSave(formikProps.values);
                                            else {
                                                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                    toastId: 'VALIDATION',
                                                    type: 'error',
                                                });
                                            }
                                        });
                                    } else {
                                        onSave(formikProps.values);
                                    }
                                }}
                            />

                            <FinalizeButton
                                mainColor={mainColor}
                                hideFinalizeBtn={hideFinalizeBtn}
                                isInternalForm={isInternalForm}
                                canEdit={canEdit}
                                onSubmit={() => {
                                    validateForm && validateForm(formikProps.validateForm());
                                }}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
}

export default injectIntl(ChangeEmailRegistration);
